<template>
    <template v-if="modelValue">
        <div>
            <template v-if="modelValue.reviewDate">
                <span>Reviewed - {{ reviewDate }}</span>
            </template>
            <span v-if="editable"
                  class="pull-right-indented">
                <span v-if="showModified"
                      class="modified-pill">
                    Modified
                </span>
                <button type="button"
                        class="btn portal-btn outline icon-button"
                        @click="reviewClicked">
                    <i class="icon icon-clipboard" />
                </button>
            </span>
            <span class="pull-right-clear">
                <display-multiline-text :text="modelValue.comment" />
            </span>
        </div>

        <div :id="editDialogId"
             class="modal fade"
             tabindex="-1"
             role="dialog"
             :aria-labelledby="editDialogTitleId"
             aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered dialog-adaptive">
                <div class="modal-content">
                    <div class="modal-header">
                        <label :id="editDialogTitleId"
                               class="modal-title">
                            <label>
                                <i class="icon-clipboard" />
                                <span>Review</span>
                            </label>
                        </label>
                    </div>
                    <div class="modal-body no-padding left-align">
                        <dl>
                            <dt>
                                Review Status
                            </dt>
                            <dd>
                                <select v-model="reviewStatusId"
                                        class="form-control full-width">
                                    <option v-for="(status, index) in reviewStatuses"
                                            :key="`review_status_${index}`"
                                            :value="status.id"
                                            class="form-control">
                                        {{ status.name }}
                                    </option>
                                </select>
                            </dd>
                            <dt>
                                Comment
                            </dt>
                            <dd>
                                <textarea v-model="comment"
                                          class="form-control full-width" />
                            </dd>
                        </dl>
                    </div>
                    <div class="modal-footer">
                        <button type="button"
                                class="btn btn-color btn-outline-primary btn-sm"
                                @click="closeReview">
                            Go Back
                        </button>
                        <UpdateButton button-label="Review"
                                      :sending="sending"
                                      :default-button="false"
                                      @click="acceptReview" />
                    </div>
                </div>
            </div>
        </div>
    </template>
</template>

<script setup>
    import { defineProps, defineEmits, computed, ref } from 'vue';
    import axios from 'axios';
    import RecoveryGoalItems from './RecoveryGoalItems.vue';
    import UpdateButton from '../UpdateButton.vue';
    import DisplayMultilineText from '../DisplayMultilineText.vue';
    import { modalDialog } from '../../js/ModalDialog.js';
    import { axiosConfig, axiosError } from '../../js/AjaxUtilities.js'
    import { toDateSentence } from '../../js/DateUtilities.js';
    import { canEdit } from '../../js/CalcGoalPlanEditState.js';



    const props = defineProps({
        modelValue: { // review statuses [{ name, id }]
            type: Object,
            default: null
        },
        patientId: {
            type: Number,
            default: 0
        },
        editState: {
            type: Number,
            default: 0
        },
        reviewStatuses: {
            type: Array,
            default: null
        },
        urlHead: {
            type: String,
            default: null
        }
    });

    const emit = defineEmits(['update:modelValue']);

    const reviewDate = computed(() => toDateSentence(new Date(Date.parse(props.modelValue.reviewDate))));
    const editDialogId = computed(() => `reviewGoalDialog-${props.modelValue.goalPlanRecoveryGoalBlockId}`);
    const editDialogTitleId = computed((() => `${editDialogId.value}Title`));
    const editable = computed(() => canEdit(props.editState, 2)); // anyone can edit
    const showModified = computed(() => props.modelValue.reviewModified && props.editState == 1);


    const sending = ref(false);
    const reviewStatusId = ref(null);
    const comment = ref(null);

    function reviewClicked() {

        let matching = props.reviewStatuses.find(r => r.name == props.modelValue.reviewStatus);

        reviewStatusId.value = matching ? matching.id : 0;
        comment.value = props.modelValue.comment;

        modalDialog.openModal(editDialogId.value);
    }

    function acceptReview() {
        sending.value = true;

        let data = {
            recoveryGoalBlockId: props.modelValue.goalPlanRecoveryGoalBlockId,
            reviewStatusId: reviewStatusId.value,
            comment: comment.value,
            patientId: props.patientId
        };


        axios
            .post(`/${props.urlHead}/ReviewGoalBlock`, data, axiosConfig())
            .then(function (response) {
                if (response.data.success) {
                    emit("update:modelValue", response.data.recoveryGoalBlock);
                } else {
                    alert(`Failed to review goal: ${response.data.message}`);
                }
            })
            .catch(function (error) {
                axiosError("Review goals", axios, error);
            })
            .finally(function () {
                sending.value = false;
                modalDialog.closeModal(editDialogId.value);
            });
    }

    function closeReview() {
        modalDialog.closeModal(editDialogId.value);
    }

</script>

<style scoped>
</style>